import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TenantService } from '../../../services/tenant.service';
import { ToastService } from '../../../services/toast.service';
import { ErrorHandlerService } from '../../../shared/services/error-handler.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { Tenant } from '../../../models/tenant.model';
import { MaterialModule } from '../../../shared/material.module';
import { TenantDialogComponent, DialogMode } from '../tenant-dialog/tenant-dialog.component';
import { TenantViewModalComponent } from '../tenant-view-modal/tenant-view-modal.component';
import { TenantModuleAssignmentComponent } from '../tenant-module-assignment/tenant-module-assignment.component';
import { TenantSetPasswordComponent } from '../tenant-set-password/tenant-set-password.component';
import { PaginationComponent, PaginationEvent } from '../../../shared/components/pagination/pagination.component';

@Component({
  selector: 'app-tenant-list',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule, PaginationComponent],
  templateUrl: './tenant-list.component.html',
  styleUrl: './tenant-list.component.scss'
})
export class TenantListComponent implements OnInit {
  tenants: Tenant[] = [];
  filteredTenants: Tenant[] = [];
  paginatedTenants: Tenant[] = [];
  searchTerm = '';
  
  // Pagination properties
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  pageSizeOptions = [5, 10, 20, 50];

  constructor(
    private tenantService: TenantService,
    private toastService: ToastService,
    private errorHandler: ErrorHandlerService,
    private confirmationService: ConfirmationService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadTenants();
  }

  loadTenants(): void {
    this.tenantService.getAllTenants().subscribe({
      next: (tenants: Tenant[]) => {
        this.tenants = tenants;
        this.filteredTenants = tenants;
        this.totalItems = tenants.length;
        this.updatePaginatedTenants();
      },
      error: (error: any) => {
        const errorResult = this.errorHandler.handleError(error);
        this.toastService.error('Error', errorResult.message);
      }
    });
  }

  filterTenants(): void {
    if (!this.searchTerm.trim()) {
      this.filteredTenants = this.tenants;
    } else {
      const term = this.searchTerm.toLowerCase();
      this.filteredTenants = this.tenants.filter(tenant =>
        tenant.name.toLowerCase().includes(term) ||
        tenant.slug.toLowerCase().includes(term) ||
        (tenant.description && tenant.description.toLowerCase().includes(term)) ||
        (tenant.contactEmail && tenant.contactEmail.toLowerCase().includes(term)) ||
        (tenant.city && tenant.city.toLowerCase().includes(term)) ||
        (tenant.country && tenant.country.toLowerCase().includes(term))
      );
    }
    
    this.totalItems = this.filteredTenants.length;
    this.currentPage = 1; // Reset to first page when filtering
    this.updatePaginatedTenants();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.filterTenants();
  }

  getActiveTenantCount(): number {
    return this.filteredTenants.filter(t => t.isActive).length;
  }

  getInactiveTenantCount(): number {
    return this.filteredTenants.filter(t => !t.isActive).length;
  }

  updatePaginatedTenants(): void {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedTenants = this.filteredTenants.slice(startIndex, endIndex);
  }

  onPageChange(event: PaginationEvent): void {
    this.currentPage = event.pageIndex + 1; // Convert from 0-based to 1-based
    this.pageSize = event.pageSize;
    this.updatePaginatedTenants();
  }

      openCreateDialog(): void {
        const dialogRef = this.dialog.open(TenantDialogComponent, {
          width: '900px',
          maxWidth: '90vw',
          disableClose: true,
          data: { mode: DialogMode.CREATE }
        });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadTenants(); // Reload the list
      }
    });
  }

      openEditDialog(tenant: Tenant): void {
        const dialogRef = this.dialog.open(TenantDialogComponent, {
          width: '900px',
          maxWidth: '90vw',
          disableClose: true,
          data: { mode: DialogMode.EDIT, tenant: tenant }
        });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadTenants(); // Reload the list
      }
    });
  }

      openViewDialog(tenant: Tenant): void {
        const dialogRef = this.dialog.open(TenantViewModalComponent, {
          width: '1000px',
          maxWidth: '95vw',
          maxHeight: '90vh',
          disableClose: true,
          panelClass: 'tenant-view-modal-panel',
          data: { tenant: tenant }
        });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadTenants(); // Reload if changes were made
      }
    });
  }

  toggleTenantStatus(tenant: Tenant): void {
    const action = tenant.isActive ? 'deactivate' : 'activate';
    const confirmMethod = tenant.isActive ? 
      this.confirmationService.confirmDeactivate(tenant.name, 'tenant') :
      this.confirmationService.confirmActivate(tenant.name, 'tenant');

    confirmMethod.subscribe({
      next: (confirmed) => {
        if (confirmed) {
          const serviceMethod = tenant.isActive ? 
            this.tenantService.deactivateTenant(tenant.id) : 
            this.tenantService.activateTenant(tenant.id);

          serviceMethod.subscribe({
            next: () => {
              tenant.isActive = !tenant.isActive;
              this.toastService.success(
                'Tenant Updated', 
                `Tenant has been ${action}d successfully`
              );
            },
            error: (error: any) => {
              const errorResult = this.errorHandler.handleError(error);
              this.toastService.error('Error', errorResult.message);
            }
          });
        }
      }
    });
  }

  deleteTenant(tenant: Tenant): void {
    this.confirmationService.confirmDelete(tenant.name, 'tenant').subscribe({
      next: (confirmed) => {
        if (confirmed) {
          this.tenantService.deleteTenant(tenant.id).subscribe({
            next: () => {
              this.tenants = this.tenants.filter(t => t.id !== tenant.id);
              this.filteredTenants = this.filteredTenants.filter(t => t.id !== tenant.id);
              this.updatePaginatedTenants();
              this.toastService.success('Tenant Deleted', 'Tenant has been deleted successfully');
            },
            error: (error: any) => {
              const errorResult = this.errorHandler.handleError(error);
              this.toastService.error('Error', errorResult.message);
            }
          });
        }
      }
    });
  }

  openModuleAssignmentDialog(tenant: Tenant): void {
    const dialogRef = this.dialog.open(TenantModuleAssignmentComponent, {
      width: '1200px',
      maxWidth: '95vw',
      disableClose: true,
      data: { tenant: tenant }
    });

    dialogRef.afterClosed().subscribe(result => {
      // Optionally refresh tenant data if needed
    });
  }

  openSetPasswordDialog(tenant: Tenant): void {
    const dialogRef = this.dialog.open(TenantSetPasswordComponent, {
      width: '500px',
      maxWidth: '90vw',
      disableClose: true,
      data: { tenant: tenant }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.toastService.success('Password Set', 'Password has been set successfully for all tenant users');
      }
    });
  }
}